/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { toFormatCurrency } from '../../../../utils/format';
import DataLoading from '../../../DataLoading';
import moment from 'moment';
import useStore from '../../../../store';

const Q_DATASET = gql`
	query get($type: MDDataSummaryReportEnumType!, $year: Float!, $month: Float!) {
		getMonthlySummaryReport(type: $type, year: $year, month: $month)
	}
`;

export default function SummarizedSpreadSheetTable({ type = null, year = null, month = null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang } = useStore((state) => state.currentLang);
	const [products, setProducts] = useState(null);
	const [localYear, setLocalYear] = useState(null);
	const [localMonth, setLocalMonth] = useState(null);
	const [getDataset, { loading, data, error }] = useLazyQuery(Q_DATASET, { fetchPolicy: 'no-cache' });

	useEffect(() => {
		setLocalYear(year ? parseInt(year) : moment().year());
		setLocalMonth(month ? parseInt(month) : moment().month());
	}, [month, year]);

	useEffect(() => {
		if (data?.getMonthlySummaryReport) {
			setProducts(data?.getMonthlySummaryReport?.duringByProduct.products);
		}
	}, [data]);

	const load = useCallback(() => {
		getDataset({
			variables: {
				type,
				year: localYear,
				month: localMonth,
			},
		});
	}, [getDataset, type, localYear, localMonth]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	return (
		<>
			<Flex justifyContent="flex-start" alignItems="center" ml={4} mr={4}>
				<Text fontSize={'15px'} fontWeight={'bold'}>
					{`${t('1. Insurance Market Data as of')} ${t(moment(localMonth + 1, 'MM').format('MMMM'))} ${t(localYear)}`}
				</Text>
			</Flex>
			<DataLoading loading={loading} loadedData={data?.getMonthlySummaryReport?.asOf}>
				{({ loadedData }) => (
					<Box ml={4} mr={4} mt={0} mb={8} overflow="scroll">
						<table className="spreadsheet_custom_header" style={{ marginTop: '10px' }}>
							<thead style={{ height: '35px' }}>
								<tr>
									<th className="sticky-col-1" style={{ minWidth: '212px', color: 'var(--chakra-colors-blue-600)' }}>
										{t('descriptionReport')}
									</th>
									{data?.getMonthlySummaryReport?.asOf?.months?.map((month) => {
										if (month === -1) {
											return <th style={{ color: 'var(--chakra-colors-blue-600)' }}>{`${t('total')}`}</th>;
										} else {
											const title = t(moment(month + 1, 'MM').format('MMMM'));
											return <th style={{ color: 'var(--chakra-colors-blue-600)' }}>{title}</th>;
										}
									})}
								</tr>
							</thead>
							<tbody>
								{/* Gross Premium */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Premium')}
									</td>
									{data?.getMonthlySummaryReport?.asOf?.data?.grosspremium?.map((obj) => (
										<th className="th-value">{toFormatCurrency(obj?.sum)}</th>
									))}
								</tr>
								{/* Number of Policies */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Number of Policies')}
									</td>
									{data?.getMonthlySummaryReport?.asOf?.data?.numpolicies?.map((obj) => (
										<th className="th-value">{toFormatCurrency(obj?.sum, null, 0)}</th>
									))}
								</tr>
								{/* Sum Insured */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Sum Insured')}
									</td>
									{data?.getMonthlySummaryReport?.asOf?.data?.suminsured?.map((obj) => (
										<th className="th-value">{toFormatCurrency(obj?.sum)}</th>
									))}
								</tr>
								{/* Gross Claim incurred */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Claim Incurred')}
									</td>
									{data?.getMonthlySummaryReport?.asOf?.data?.claimincurred?.map((obj) => (
										<th className="th-value">{toFormatCurrency(obj?.sum)}</th>
									))}
								</tr>
								{/* Gross Claim Paid */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Claim Paid')}
									</td>
									{data?.getMonthlySummaryReport?.asOf?.data?.claimpaid?.map((obj) => (
										<th className="th-value">{toFormatCurrency(obj?.sum)}</th>
									))}
								</tr>
							</tbody>
						</table>
					</Box>
				)}
			</DataLoading>

			<Flex justifyContent="flex-start" alignItems="center" ml={4} mr={4}>
				<Text fontSize={'15px'} fontWeight={'bold'}>
					{`${t('2. Insurance Market Data in')} ${t(moment(localMonth + 1, 'MM').format('MMMM'))} ${t(localYear)}`}
				</Text>
			</Flex>
			<DataLoading loading={loading} loadedData={data?.getMonthlySummaryReport?.during}>
				{({ loadedData }) => (
					<Box ml={4} mr={4} mt={0} mb={8} overflow="scroll">
						<table className="spreadsheet_custom_header" style={{ marginTop: '10px' }}>
							<thead style={{ height: '35px' }}>
								<tr>
									<th className="sticky-col-1" style={{ minWidth: '212px', color: 'var(--chakra-colors-blue-600)' }}>
										{t('descriptionReport')}
									</th>
									<td style={{ color: 'var(--chakra-colors-blue-600)' }}>{localYear - 1}</td>
									<td style={{ color: 'var(--chakra-colors-blue-600)' }}>{localYear}</td>
									<td style={{ color: 'var(--chakra-colors-blue-600)' }}>{t('comparison')}</td>
								</tr>
							</thead>
							<tbody>
								{/* Premium */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Premium')}
									</td>
									<th className="th-value">{toFormatCurrency(loadedData?.grosspremium?.[localYear - 1])}</th>
									<th className="th-value">{toFormatCurrency(loadedData?.grosspremium?.[localYear])}</th>
									<th className="th-value">{loadedData?.grosspremium?.['comparison']}</th>
								</tr>
								{/* Number of Policies */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Number of Policies')}
									</td>
									<th className="th-value">{toFormatCurrency(loadedData?.numpolicies?.[localYear - 1], null, 0)}</th>
									<th className="th-value">{toFormatCurrency(loadedData?.numpolicies?.[localYear], null, 0)}</th>
									<th className="th-value">{loadedData?.numpolicies?.['comparison']}</th>
								</tr>
								{/* Sum Insured */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Sum Insured')}
									</td>
									<th className="th-value">{toFormatCurrency(loadedData?.suminsured?.[localYear - 1])}</th>
									<th className="th-value">{toFormatCurrency(loadedData?.suminsured?.[localYear])}</th>
									<th className="th-value">{loadedData?.suminsured?.['comparison']}</th>
								</tr>
								{/* Gross Claim incurred */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Claim Incurred')}
									</td>
									<th className="th-value">{toFormatCurrency(loadedData?.claimincurred?.[localYear - 1])}</th>
									<th className="th-value">{toFormatCurrency(loadedData?.claimincurred?.[localYear])}</th>
									<th className="th-value">{loadedData?.claimincurred?.['comparison']}</th>
								</tr>
								{/* Gross Claim Paid */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" style={{ minWidth: '212px' }}>
										{t('Gross Claim Paid')}
									</td>
									<th className="th-value">{toFormatCurrency(loadedData?.claimpaid?.[localYear - 1])}</th>
									<th className="th-value">{toFormatCurrency(loadedData?.claimpaid?.[localYear])}</th>
									<th className="th-value">{loadedData?.claimpaid?.['comparison']}</th>
								</tr>
							</tbody>
						</table>
					</Box>
				)}
			</DataLoading>
			<Flex justifyContent="flex-start" alignItems="center" ml={4} mr={4}>
				<Text fontSize={'15px'} fontWeight={'bold'}>
					{`${t('3. Type of Insurance Product by')} ${t(moment(localMonth + 1, 'MM').format('MMMM'))} ${t(localYear)}`}
				</Text>
			</Flex>
			<DataLoading loading={loading} loadedData={data?.getMonthlySummaryReport?.duringByProduct}>
				{({ loadedData }) => (
					<Box ml={4} mr={4} mt={0} mb={0} overflow="scroll">
						<table className="spreadsheet_custom_header_report" style={{ marginTop: '10px' }}>
							<thead>
								<tr>
									<th className="sticky-col-1" style={{ color: 'var(--chakra-colors-blue-600)' }}>
										{t('descriptionReport')}
									</th>
									<td style={{ color: 'var(--chakra-colors-blue-600)' }} className="sticky-col-3">
										{t('Year')}
									</td>
									{products?.map((el, i) => {
										return (
											<td style={{ color: 'var(--chakra-colors-blue-600)' }}>
												{(() => {
													const text = currentLang === 'en' ? el?.productNameEn : el?.productNameKh;
													const index = text.indexOf('(');
													return index !== -1 ? text.substring(0, index).trim() : text;
												})()}
											</td>
										);
									})}
								</tr>
							</thead>
							<tbody>
								{/* Gross Premium */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={2} style={{ minWidth: '200px' }}>
										{t('Gross Premium')}
									</td>
									<th className="th-label-total sticky-col-3">{localYear - 1}</th>
									{loadedData?.grosspremium[localYear - 1]?.map((el, i) => {
										if (i === loadedData?.grosspremium[localYear - 1]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								<tr height={'35px'}>
									<th className="th-label-total sticky-col-3">{localYear}</th>
									{loadedData?.grosspremium[localYear]?.map((el, i) => {
										if (i === loadedData?.grosspremium[localYear]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								{/* Number of Policies */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={2} style={{ minWidth: '200px' }}>
										{t('Number of Policies')}
									</td>
									<th className="th-label-total sticky-col-3">{localYear - 1}</th>
									{loadedData?.numpolicies[localYear - 1]?.map((el, i) => {
										if (i === loadedData?.numpolicies[localYear - 1]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el, null, 0)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el, null, 0)}
											</th>
										);
									})}
								</tr>
								<tr height={'35px'}>
									<th className="th-label-total sticky-col-3">{localYear}</th>
									{loadedData?.numpolicies[localYear]?.map((el, i) => {
										if (i === loadedData?.numpolicies[localYear]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el, null, 0)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el, null, 0)}
											</th>
										);
									})}
								</tr>
								{/* Sum Insured */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={2} style={{ minWidth: '200px' }}>
										{t('Sum Insured')}
									</td>
									<th className="th-label-total sticky-col-3">{localYear - 1}</th>
									{loadedData?.suminsured[localYear - 1]?.map((el, i) => {
										if (i === loadedData?.suminsured[localYear - 1]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								<tr height={'35px'}>
									<th className="th-label-total sticky-col-3">{localYear}</th>
									{loadedData?.suminsured[localYear]?.map((el, i) => {
										if (i === loadedData?.suminsured[localYear]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								{/* Gross Claim incurred */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={2} style={{ minWidth: '200px', position: 'sticky' }}>
										{t('Gross Claim Incurred')}
									</td>
									<th className="th-label-total sticky-col-3">{localYear - 1}</th>
									{loadedData?.claimincurred[localYear - 1]?.map((el, i) => {
										if (i === loadedData?.claimincurred[localYear - 1]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								<tr height={'35px'}>
									<th className="th-label-total sticky-col-3">{localYear}</th>
									{loadedData?.claimincurred[localYear]?.map((el, i) => {
										if (i === loadedData?.claimincurred[localYear]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								{/* Gross Claim Paid */}
								<tr height={'35px'} style={{ minWidth: '200px', position: 'sticky' }}>
									<td className="th-label sticky-col-1" rowSpan={2}>
										{t('Gross Claim Paid')}
									</td>
									<th className="th-label-total sticky-col-3">{localYear - 1}</th>
									{loadedData?.claimpaid[localYear - 1]?.map((el, i) => {
										if (i === loadedData?.claimpaid[localYear - 1]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
								<tr height={'35px'}>
									<th className="th-label-total sticky-col-3">{localYear}</th>
									{loadedData?.claimpaid[localYear]?.map((el, i) => {
										if (i === loadedData?.claimpaid[localYear]?.length - 1) {
											return (
												<th key={i} className="th-value-total">
													{toFormatCurrency(el)}
												</th>
											);
										}
										return (
											<th key={i} className="th-value">
												{toFormatCurrency(el)}
											</th>
										);
									})}
								</tr>
							</tbody>
						</table>
					</Box>
				)}
			</DataLoading>
		</>
	);
}
